import { useCallback, useEffect, useMemo, useRef, type RefObject } from "react";

import type { VirtualListHandle } from "@tsmono/react/virtual";

import type { TurnInfo } from "../outline/tree-visitors";
import { flatTree } from "../transform/flatten";
import { pairToolApprovals } from "../transform/toolApprovals";
import { pairToolSentinels } from "../transform/toolSentinels";
import {
  anchorIndexForTurn,
  computeTranscriptTurns,
  focusedTurnNodes,
  resolveEventTurnAnchor,
} from "../turnNavigation";
import type { EventNode, EventNodeContext } from "../types";

import type { FocusLane, FocusLaneScope } from "./useFocusLaneScope";
import { useTranscriptKeyboardNavigation } from "./useTranscriptKeyboardNavigation";

/**
 * The `setParams` both focus pages hand to {@link useFocusTurnNavigation}:
 * applies all updates to the query string in ONE replace navigation (null
 * deletes a key; consecutive router writes don't compose, so disarm+advance
 * must be atomic). Pass react-router's `setSearchParams`.
 */
export function useFocusSetParams(
  setSearchParams: (
    updater: (prev: URLSearchParams) => URLSearchParams,
    opts?: { replace?: boolean }
  ) => void
): (updates: Record<string, string | null>) => void {
  return useCallback(
    (updates: Record<string, string | null>) =>
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null) params.delete(key);
            else params.set(key, value);
          }
          return params;
        },
        { replace: true }
      ),
    [setSearchParams]
  );
}

export interface FocusTabValue {
  /** Currently selected tab NAME (e.g. "API"), remembered across events. */
  tab: string;
  setTab: (tab: string) => void;
}

/** One segment of the focused lane's agent-path breadcrumb (root → lane). */
export interface LaneCrumb {
  label: string;
  /** Navigate to this ancestor lane's first turn. Absent on the last (focused)
   *  segment and on ancestors with no turn to land on. */
  onSelect?: () => void;
}

/** One entry in the lane picker: every lane, in row order. */
export interface LaneOption {
  label: string;
  /** Nesting depth (0 = root) for indentation in the picker list. */
  depth: number;
  isCurrent: boolean;
  /** Navigate to this lane's first turn. */
  onSelect: () => void;
}

export interface FocusTurnNavigation {
  scrollRef: RefObject<HTMLDivElement | null>;
  listHandle: RefObject<VirtualListHandle | null>;
  /** The focused turn's events (model + its tools), fully expanded. */
  slice: EventNode[];
  /** The slice's approvals and sentinel steps, which render inside their tools rather than as rows of the slice. */
  checks: Pick<
    EventNodeContext,
    "toolApprovals" | "toolSentinels" | "standaloneSentinels"
  >;
  /** The focused event id resolved to its turn anchor (see hook docs). Use for
   *  exit-focus navigation so leaving lands on the turn actually shown. */
  resolvedEventId: string | null;
  /** Turn number/total from the lane's transcript turn map (matches the main
   *  view's numbering for the same swimlane scope). */
  turnInfo: TurnInfo | undefined;
  /** Index of the focused event's turn among the turn anchors, or -1. */
  turnIndex: number;
  /** Total turns shown in the focused lane's labels and go-to-turn range. */
  totalTurns: number;
  /** Number of visible turn anchors available to prev/next navigation. */
  navigableTurnCount: number;
  onPrev: () => void;
  onNext: () => void;
  /** Whether the next/prev turn buttons should act — mirrors stepTurn's own
   *  arm/disarm branches so buttons and the j/k keys agree. */
  canStepNext: boolean;
  canStepPrev: boolean;
  /** Jump to a 1-based turn number (clamped), for the Ctrl/Cmd+G bar. */
  goToTurn: (turnNumber: number) => void;
  /** Agent-path breadcrumb for the focused lane, root first. The last
   *  segment is the focused lane itself; ancestors carry an onSelect. */
  laneCrumbs: LaneCrumb[];
  /** All lanes for the click-to-select picker, in row order. */
  laneOptions: LaneOption[];
  /** Selected view tab + setter, both URL-backed (single source of truth). */
  focusTab: FocusTabValue;
  /** Follow-latest is armed and the sample is running: the focus tracks the
   *  newest turn as it arrives (label reads "following latest turn N/N"). */
  followingLatest: boolean;
}

/**
 * Turn navigation for the focus-mode page (single focused turn), shared by
 * the inspect and scout single-event pages. The focused event and selected
 * tab live in the URL (the single source of truth), written via
 * {@link useFocusSetParams}. `eventNodes` / `defaultCollapsedIds` must be
 * built from the focused lane's scoped events (`useFocusLaneScope().laneEvents`
 * → `useEventNodes`) so "turn N/M" always agrees with the transcript view of
 * that swimlane.
 */
export function useFocusTurnNavigation(
  eventNodes: EventNode[],
  eventId: string | null,
  tab: string,
  setParams: (updates: Record<string, string | null>) => void,
  defaultCollapsedIds?: Record<string, boolean> | null,
  laneScope?: FocusLaneScope,
  options?: {
    /** The sample is still producing events (running/streaming). */
    running?: boolean;
    /** Follow-latest is armed (URL `follow=1`): each newly arrived turn
     *  auto-advances the focus; any explicit navigation disarms. */
    following?: boolean;
  }
): FocusTurnNavigation {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const listHandle = useRef<VirtualListHandle | null>(null);

  const flat = useMemo(() => flatTree(eventNodes, null), [eventNodes]);

  // The focused lane's turns, computed exactly the way the transcript view
  // computes them for that lane's swimlane scope, so "turn N/M" agrees.
  const { turnMap, anchorIds } = useMemo(
    () => computeTranscriptTurns(eventNodes, flat, defaultCollapsedIds ?? null),
    [eventNodes, flat, defaultCollapsedIds]
  );

  // Resolve a tool/sandbox target to its enclosing turn's model anchor, so
  // the focus page always shows the WHOLE turn, never a bare tool slice.
  const resolvedEventId = useMemo(() => {
    // Following-latest lands directly on the newest turn so a focus mount never
    // flashes an early turn while the URL `?event=` catches up to the tail.
    if (options?.following && anchorIds.length > 0)
      return anchorIds[anchorIds.length - 1] ?? null;
    // No event param (a parameterless focus URL, or prev/next onto a sibling
    // sample) defaults to the first turn anchor, so the page loads the first
    // turn instead of rendering nothing.
    if (!eventId) return anchorIds[0] ?? null;
    return resolveEventTurnAnchor(flat, eventId) ?? eventId;
  }, [eventId, anchorIds, flat, options?.following]);

  // Paired over the whole lane: the slice drops span markers, and with them
  // the sentinel spans that tie monitor model calls to their step.
  const pairing = useMemo(
    () => ({ ...pairToolApprovals(flat), ...pairToolSentinels(flat) }),
    [flat]
  );
  const { slice, checks } = useMemo(() => {
    const {
      toolApprovals,
      hiddenApprovalIds,
      toolSentinels,
      standaloneSentinels,
      hiddenSentinelIds,
    } = pairing;
    const nodes = resolvedEventId
      ? focusedTurnNodes(flat, resolvedEventId, (n) =>
          standaloneSentinels.has(n.id)
        )
      : [];
    return {
      slice: nodes.filter(
        (n) => !hiddenSentinelIds.has(n.id) && !hiddenApprovalIds.has(n.id)
      ),
      checks: { toolApprovals, toolSentinels, standaloneSentinels },
    };
  }, [flat, resolvedEventId, pairing]);

  const turnInfo: TurnInfo | undefined = resolvedEventId
    ? turnMap.get(resolvedEventId)
    : undefined;
  // Anchor index for stepping. A focused event inside a turn (e.g. a model
  // hidden inside a default-collapsed span) inherits its enclosing turn's
  // number, so j/k step from THAT turn rather than being disabled.
  const turnIndex = turnInfo
    ? anchorIndexForTurn(anchorIds, turnMap, turnInfo.turnNumber)
    : -1;
  const totalTurns =
    (anchorIds[0] ? turnMap.get(anchorIds[0])?.totalTurns : undefined) ??
    anchorIds.length;

  const running = !!options?.running;
  const following = !!options?.following;
  // Mirror the `following` URL param in a synchronously-updated ref: the
  // param round-trips through setParams + a re-render, so back-to-back key
  // handlers in one tick would otherwise miss their own just-armed state.
  const followingRef = useRef(following);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    followingRef.current = following;
  }, [following]);
  const stepTurn = useCallback(
    (delta: 1 | -1) => {
      const isFollowing = followingRef.current;
      const next = anchorIds[turnIndex + delta];
      if (delta === 1 && next === undefined && running && !isFollowing) {
        // One more j past the last turn of a RUNNING sample arms
        // follow-latest (the focus-mode analog of the transcript's
        // pin-to-bottom); explicit navigation below disarms it.
        followingRef.current = true;
        setParams({ follow: "1" });
        return;
      }
      // k always disarms, even when there's no earlier turn to land on.
      if (delta === -1 && isFollowing && next === undefined) {
        followingRef.current = false;
        setParams({ follow: null });
        return;
      }
      if (turnIndex === -1 || next === undefined) return;
      // Only a `k` step while following actually clears follow here — a
      // manual `j` while following leaves it armed (moving the same
      // direction follow already tracks).
      const disarming = delta === -1 && isFollowing;
      if (disarming) followingRef.current = false;
      setParams(disarming ? { event: next, follow: null } : { event: next });
    },
    [turnIndex, anchorIds, setParams, running]
  );
  const onPrev = useCallback(() => stepTurn(-1), [stepTurn]);
  const onNext = useCallback(() => stepTurn(1), [stepTurn]);

  // Enabled exactly when stepTurn would act: a neighbouring anchor to land on,
  // or the follow arm/disarm edge (last turn of a running sample arms; a
  // following state disarms even with no earlier turn).
  const canStepNext =
    (turnIndex !== -1 && anchorIds[turnIndex + 1] !== undefined) ||
    (anchorIds[turnIndex + 1] === undefined && running && !following);
  const canStepPrev =
    (turnIndex !== -1 && anchorIds[turnIndex - 1] !== undefined) ||
    (anchorIds[turnIndex - 1] === undefined && following);

  const goToTurn = useCallback(
    (turnNumber: number) => {
      if (anchorIds.length === 0) return;
      const anchor =
        anchorIds[anchorIndexForTurn(anchorIds, turnMap, turnNumber)];
      if (anchor !== undefined) {
        setParams(
          following ? { event: anchor, follow: null } : { event: anchor }
        );
      } else if (following) {
        setParams({ follow: null });
      }
    },
    [anchorIds, turnMap, setParams, following]
  );

  // Lane navigation mirrors the transcript's swimlane row clicks: switching
  // lanes parks `?event=` on the target lane's first turn anchor, so the
  // lane stays derivable from the URL.
  const lanes = useMemo(() => laneScope?.lanes ?? [], [laneScope]);
  const laneIndex = laneScope?.laneIndex ?? 0;
  const goToLane = useCallback(
    (lane: FocusLane | undefined) => {
      if (!lane?.firstAnchorId) return;
      setParams(
        following
          ? { event: lane.firstAnchorId, follow: null }
          : { event: lane.firstAnchorId }
      );
    },
    [setParams, following]
  );
  const nextLane = useCallback(
    (delta: 1 | -1): FocusLane | undefined => {
      for (let i = laneIndex + delta; i >= 0 && i < lanes.length; i += delta) {
        if (lanes[i]?.firstAnchorId) return lanes[i];
      }
      return undefined;
    },
    [lanes, laneIndex]
  );
  const onPrevAgent = useCallback(
    () => goToLane(nextLane(-1)),
    [goToLane, nextLane]
  );
  const onNextAgent = useCallback(
    () => goToLane(nextLane(1)),
    [goToLane, nextLane]
  );

  const laneCrumbs = useMemo<LaneCrumb[]>(() => {
    const path = laneScope?.lanePath ?? [];
    if (path.length === 0) return [{ label: "main" }];
    return path.map((lane, i) => {
      const isLast = i === path.length - 1;
      return !isLast && lane.firstAnchorId
        ? { label: lane.label, onSelect: () => goToLane(lane) }
        : { label: lane.label };
    });
  }, [laneScope, goToLane]);

  const laneOptions = useMemo<LaneOption[]>(
    () =>
      lanes
        .filter((lane) => lane.firstAnchorId)
        .map((lane) => ({
          label: lane.label,
          depth: lane.depth,
          isCurrent: lane.isCurrent,
          onSelect: () => goToLane(lane),
        })),
    [lanes, goToLane]
  );

  const onFirst = useCallback(() => goToTurn(1), [goToTurn]);
  const onLast = useCallback(
    () => goToTurn(totalTurns),
    [goToTurn, totalTurns]
  );

  useTranscriptKeyboardNavigation({
    listHandle,
    scrollRef,
    itemCount: slice.length,
    onPrev,
    onNext,
    onPrevAgent: lanes.length > 1 ? onPrevAgent : undefined,
    onNextAgent: lanes.length > 1 ? onNextAgent : undefined,
    onFirst: anchorIds.length > 0 ? onFirst : undefined,
    onLast: anchorIds.length > 0 ? onLast : undefined,
  });

  // Follow-latest parks `?event=` on the newest anchor. Deliberately NOT
  // gated on `running`: the final turn batch and the settled status arrive
  // in the same update, so a running gate would stop one turn short.
  const lastAnchor = anchorIds[anchorIds.length - 1];
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (following && lastAnchor && eventId !== lastAnchor) {
      setParams({ event: lastAnchor });
    }
  }, [following, lastAnchor, eventId, setParams]);
  const followingLatest = following && running;

  const focusTab = useMemo<FocusTabValue>(
    () => ({ tab, setTab: (next: string) => setParams({ tab: next }) }),
    [tab, setParams]
  );

  return {
    scrollRef,
    listHandle,
    slice,
    checks,
    resolvedEventId,
    followingLatest,
    turnInfo,
    turnIndex,
    totalTurns,
    navigableTurnCount: anchorIds.length,
    onPrev,
    onNext,
    canStepNext,
    canStepPrev,
    goToTurn,
    laneCrumbs,
    laneOptions,
    focusTab,
  };
}
